import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import SpotCard from "@/components/SpotCard";
import { Map, Package, IdCard, Compass, ArrowRight, Fish, CloudSun } from "lucide-react";

const hero = "https://images.unsplash.com/photo-1782749869445-7cd50cf4b1d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const features = [
  { icon: Map, title: "Carte interactive", desc: "Explorez les meilleurs spots de France sur une carte filtrable par poisson, région et type d'eau.", to: "/spots", color: "text-emerald-600" },
  { icon: Package, title: "Kits d'équipement", desc: "Des kits prêts à l'emploi chez Decathlon, Intersport, E.Leclerc, Pacific Pêche et Pecheur.com selon votre pêche.", to: "/equipement", color: "text-amber-600" },
  { icon: IdCard, title: "Carte de pêche", desc: "Obtenez votre carte officielle sur cartedepeche.fr en quelques clics, adaptée à votre profil.", to: "/carte-de-peche", color: "text-sky-600" },
];

export default function Home() {
  const [spots, setSpots] = useState([]);

  useEffect(() => {
    api.get("/spots").then((r) => setSpots(r.data.slice(0, 6)));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center grain-overlay">
        <img src={hero} alt="Pêche" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-emerald-950/60 to-emerald-950/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur border border-emerald-400/30 text-emerald-100 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase">
              <Compass className="w-4 h-4" /> Le compagnon des pêcheurs en France
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
              Trouvez les meilleurs <span className="text-emerald-400">spots de pêche</span> de France
            </h1>
            <p className="mt-5 text-lg text-emerald-100/85 leading-relaxed max-w-xl">
              Carte interactive, météo en direct, kits d'équipement et carte de pêche officielle.
              Tout pour préparer votre prochaine sortie.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/spots"
                data-testid="hero-explore-btn"
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-bold py-3.5 px-7 rounded-full shadow-lg transition-all hover:-translate-y-1"
              >
                <Map className="w-5 h-5" /> Explorer la carte
              </Link>
              <Link
                to="/equipement"
                data-testid="hero-kits-btn"
                className="flex items-center gap-2 bg-white/10 backdrop-blur border border-white/25 hover:bg-white/20 text-white font-semibold py-3.5 px-7 rounded-full transition-all"
              >
                <Package className="w-5 h-5" /> Voir les kits
              </Link>
            </div>
            <div className="mt-10 flex gap-8">
              {[
                { n: "75+", l: "Spots référencés" },
                { n: "8", l: "Kits d'équipement" },
                { n: "IA", l: "Gratuite & privée" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-display text-3xl font-extrabold text-emerald-400">{s.n}</p>
                  <p className="text-xs text-emerald-100/70 uppercase tracking-wide">{s.l}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-24">
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <Link
                to={f.to}
                data-testid={`feature-${f.to.replace("/", "")}`}
                className="group block h-full rounded-3xl bg-white border border-emerald-900/10 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-emerald-50 mb-5">
                  <f.icon className={`w-7 h-7 ${f.color}`} />
                </span>
                <h3 className="font-display text-xl font-bold text-emerald-950">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 mt-4 group-hover:gap-2 transition-all">
                  Découvrir <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED SPOTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700/80 flex items-center gap-2">
              <Fish className="w-4 h-4" /> Sélection
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-950 mt-1">Spots à découvrir</h2>
          </div>
          <Link to="/spots" data-testid="see-all-spots" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:gap-2 transition-all">
            Tous les spots <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {spots.map((s) => (
            <SpotCard key={s.id} spot={s} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-emerald-900 text-white p-10 lg:p-14 grain-overlay">
          <div className="relative max-w-xl">
            <CloudSun className="w-10 h-10 text-amber-400 mb-4" />
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Prêt à partir pêcher ?</h2>
            <p className="text-emerald-100/80 mt-3">
              Consultez la météo de chaque spot, préparez votre kit et obtenez votre carte de pêche officielle.
            </p>
            <a
              href="https://www.cartedepeche.fr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-buy-license"
              className="inline-flex items-center gap-2 mt-6 bg-amber-500 hover:bg-amber-600 text-emerald-950 font-bold py-3.5 px-7 rounded-full transition-all hover:-translate-y-1"
            >
              <IdCard className="w-5 h-5" /> Acheter ma carte de pêche
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
